import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";

import { useAdminBundles } from "@/hooks/useBundles";
import { useCourseList } from "@/hooks/useCourses";
import { deleteBundle, upsertBundle } from "@/lib/api";

import { AdminPanelHeader } from "./AdminPanelHeader";
import { FileUpload } from "./FileUpload";

type BundleFormState = {
  id?: string;
  name: string;
  price: string;
  discount_percentage: string;
  short_description: string;
  long_description: string;
  thumbnail_url: string;
  music_url: string;
  is_active: boolean;
  course_ids: string[];
};

const EMPTY_FORM: BundleFormState = {
  name: "",
  price: "149",
  discount_percentage: "",
  short_description: "",
  long_description: "",
  thumbnail_url: "",
  music_url: "",
  is_active: true,
  course_ids: [],
};

const signatureRoutineLabel = "Signature Routine";
const signatureRoutinePluralLabel = "Signature Routines";
const signatureRoutineLabelLower = "signature routine";
const signatureRoutinePluralLabelLower = "signature routines";

export function BundleManager() {
  const { data: bundles, loading, error: bundlesError, refetch } = useAdminBundles();
  const { data: courseRows, loading: coursesLoading, error: coursesError } = useCourseList();
  const courses = courseRows ?? [];

  const [editingBundleId, setEditingBundleId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<BundleFormState>(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<HTMLFormElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const priceInputRef = useRef<HTMLInputElement | null>(null);
  const discountInputRef = useRef<HTMLInputElement | null>(null);
  const actionErrorRef = useRef<HTMLDivElement | null>(null);
  const editingBundle = useMemo(
    () => bundles?.find((entry) => entry.id === editingBundleId) ?? null,
    [bundles, editingBundleId],
  );

  useEffect(() => {
    if (editingBundleId === null) {
      setForm(EMPTY_FORM);
      setError(null);
      setSearch("");
      return;
    }

    if (editingBundleId === "new") {
      setForm(EMPTY_FORM);
      setError(null);
      setSearch("");
      return;
    }

    if (!editingBundle) return;

    setForm({
      id: editingBundle.id,
      name: editingBundle.name,
      price: String(editingBundle.price),
      discount_percentage: editingBundle.discount_percentage == null ? "" : String(editingBundle.discount_percentage),
      short_description: editingBundle.short_description ?? "",
      long_description: editingBundle.long_description ?? "",
      thumbnail_url: editingBundle.thumbnail_url ?? "",
      music_url: editingBundle.music_url ?? "",
      is_active: editingBundle.is_active,
      course_ids: editingBundle.courses.map((course) => course.id),
    });
    setError(null);
    setSearch("");
  }, [editingBundle, editingBundleId]);

  const selectedCourses = useMemo(() => {
    const courseMap = new Map(courses.map((course) => [course.id, course]));
    return form.course_ids
      .map((courseId) => courseMap.get(courseId))
      .filter((course): course is NonNullable<typeof course> => Boolean(course));
  }, [courses, form.course_ids]);

  const availableCourses = useMemo(() => {
    const query = search.trim().toLowerCase();
    const selectedIds = new Set(form.course_ids);

    return courses.filter((course) => {
      if (selectedIds.has(course.id)) return false;
      if (!query) return true;

      return (
        course.name.toLowerCase().includes(query) ||
        course.short_description?.toLowerCase().includes(query)
      );
    });
  }, [courses, form.course_ids, search]);

  const handleAddCourse = (courseId: string) => {
    setForm((current) => ({
      ...current,
      course_ids: current.course_ids.includes(courseId)
        ? current.course_ids
        : [...current.course_ids, courseId],
    }));
  };

  const handleRemoveCourse = (courseId: string) => {
    setForm((current) => ({
      ...current,
      course_ids: current.course_ids.filter((id) => id !== courseId),
    }));
  };

  const handleMoveCourse = (index: number, direction: -1 | 1) => {
    setForm((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.course_ids.length) return current;

      const courseIds = [...current.course_ids];
      const [courseId] = courseIds.splice(index, 1);
      courseIds.splice(nextIndex, 0, courseId!);
      return { ...current, course_ids: courseIds };
    });
  };

  const scrollIntoViewIfSupported = (element: { scrollIntoView?: (options?: ScrollIntoViewOptions) => void } | null) => {
    if (!element || typeof element.scrollIntoView !== "function") return;
    element.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const revealError = (message: string, options?: { focus?: HTMLInputElement | null; scrollToTop?: boolean }) => {
    setError(message);

    if (options?.scrollToTop) {
      scrollIntoViewIfSupported(editorRef.current);
    } else {
      scrollIntoViewIfSupported(actionErrorRef.current);
    }

    options?.focus?.focus();
  };

  const handleSave = async () => {
    setError(null);

    if (!form.name.trim()) {
      revealError(`${signatureRoutineLabel} name is required.`, {
        focus: nameInputRef.current,
        scrollToTop: true,
      });
      return;
    }

    const price = Number(form.price);
    if (!Number.isFinite(price) || price < 0) {
      revealError(`Enter a valid ${signatureRoutineLabelLower} price.`, {
        focus: priceInputRef.current,
        scrollToTop: true,
      });
      return;
    }

    const discountPercentage = form.discount_percentage.trim() === ""
      ? null
      : Number(form.discount_percentage);

    if (
      discountPercentage !== null &&
      (!Number.isFinite(discountPercentage) || discountPercentage < 0 || discountPercentage > 100)
    ) {
      revealError("Enter a valid discount percentage between 0 and 100.", {
        focus: discountInputRef.current,
        scrollToTop: true,
      });
      return;
    }

    if (form.course_ids.length < 2) {
      revealError(`${signatureRoutinePluralLabel} must include at least two tutorials.`);
      return;
    }

    setSaving(true);
    const result = await upsertBundle({
      id: form.id,
      name: form.name,
      price,
      discount_percentage: discountPercentage,
      short_description: form.short_description,
      long_description: form.long_description,
      thumbnail_url: form.thumbnail_url,
      music_url: form.music_url,
      is_active: form.is_active,
      course_ids: form.course_ids,
    });
    setSaving(false);

    if (result.error) {
      revealError(result.error.message ?? `Could not save ${signatureRoutineLabelLower}.`);
      return;
    }

    await refetch();
    setEditingBundleId(null);
  };

  const handleDelete = async (bundleId: string, bundleName: string) => {
    if (!confirm(`Delete "${bundleName}"? Purchased ${signatureRoutinePluralLabelLower} will be archived instead.`)) {
      return;
    }

    setDeleting(bundleId);
    const result = await deleteBundle(bundleId);
    setDeleting(null);

    if (result.error) {
      setError(result.error.message ?? `Could not delete ${signatureRoutineLabelLower}.`);
      return;
    }

    await refetch();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-dark border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <AdminPanelHeader
          title={signatureRoutinePluralLabel}
          description={`Create and manage ${signatureRoutinePluralLabelLower} for the Dance Library. Set the price directly and add an optional discount label for promotions.`}
          action={
            editingBundleId === null ? (
              <button
                type="button"
                onClick={() => setEditingBundleId("new")}
                className="inline-flex items-center gap-2 rounded-xl bg-pink-dark px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95"
              >
                <Plus className="h-4 w-4" />
                New {signatureRoutineLabel}
              </button>
            ) : null
          }
        />
      </div>

      {error && editingBundleId === null ? (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {editingBundleId !== null ? (
        <form
          ref={editorRef}
          autoComplete="off"
          className="rounded-xl border border-gray-200 bg-white p-5"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          <button
            type="button"
            onClick={() => setEditingBundleId(null)}
            className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-pink-dark"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {signatureRoutinePluralLabelLower}
          </button>

          <h3 className="mb-5 text-xl font-semibold text-gray-900">
            {editingBundleId === "new" ? `New ${signatureRoutineLabel}` : `Edit ${signatureRoutineLabel}`}
          </h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="bundle-name" className="mb-1 block text-sm font-medium text-gray-700">{signatureRoutineLabel} Name</label>
              <input
                id="bundle-name"
                ref={nameInputRef}
                type="text"
                autoComplete="off"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
              <p className="mt-1 text-xs text-gray-500">Customer-facing {signatureRoutineLabelLower} title shown in the Dance Library.</p>
            </div>

            <div>
              <label htmlFor="bundle-price" className="mb-1 block text-sm font-medium text-gray-700">{signatureRoutineLabel} Price</label>
              <input
                id="bundle-price"
                ref={priceInputRef}
                type="number"
                min="0"
                step="0.01"
                autoComplete="off"
                value={form.price}
                onChange={(event) => setForm((current) => ({ ...current, price: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
              <p className="mt-1 text-xs text-gray-500">
                Set the customer-facing {signatureRoutineLabelLower} price. New {signatureRoutinePluralLabelLower} start at $149, and you can change that anytime.
              </p>
            </div>

            <div>
              <label htmlFor="bundle-discount" className="mb-1 block text-sm font-medium text-gray-700">Discount Percentage</label>
              <input
                id="bundle-discount"
                ref={discountInputRef}
                type="number"
                min="0"
                max="100"
                step="0.01"
                autoComplete="off"
                value={form.discount_percentage}
                onChange={(event) => setForm((current) => ({ ...current, discount_percentage: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
              <p className="mt-1 text-xs text-gray-500">Optional marketing label shown above the price. Example: <code>40</code> for {`“${signatureRoutineLabel} at 40% discount”.`}</p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="bundle-short-description" className="mb-1 block text-sm font-medium text-gray-700">Short Description</label>
              <textarea
                id="bundle-short-description"
                rows={2}
                value={form.short_description}
                onChange={(event) => setForm((current) => ({ ...current, short_description: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="bundle-long-description" className="mb-1 block text-sm font-medium text-gray-700">Long Description</label>
              <textarea
                id="bundle-long-description"
                rows={5}
                value={form.long_description}
                onChange={(event) => setForm((current) => ({ ...current, long_description: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="bundle-thumbnail-url" className="mb-1 block text-sm font-medium text-gray-700">Thumbnail Image</label>
              <div className="flex items-center gap-2">
                <input
                  id="bundle-thumbnail-url"
                  type="text"
                  autoComplete="off"
                  value={form.thumbnail_url}
                  onChange={(event) => setForm((current) => ({ ...current, thumbnail_url: event.target.value }))}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                />
                <FileUpload
                  folder="bundle-images"
                  accept="image/*"
                  label="Upload"
                  onUploaded={(url) => setForm((current) => ({ ...current, thumbnail_url: url }))}
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="bundle-music-url" className="mb-1 block text-sm font-medium text-gray-700">Signature Routine MP3</label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  id="bundle-music-url"
                  type="text"
                  autoComplete="off"
                  value={form.music_url}
                  placeholder="Upload an MP3"
                  onChange={(event) => setForm((current) => ({ ...current, music_url: event.target.value }))}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                />
                <div className="flex items-center gap-2">
                  <FileUpload
                    folder="signature-routine-music"
                    accept="audio/mpeg,audio/mp3"
                    label="Upload"
                    onUploaded={(url) => setForm((current) => ({ ...current, music_url: url }))}
                  />
                  {form.music_url ? (
                    <button
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, music_url: "" }))}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:col-span-2">
              <input
                id="bundle-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-pink-dark focus:ring-pink-dark"
              />
              <label htmlFor="bundle-active" className="text-sm font-medium text-gray-700">
                Active in Dance Library
              </label>
            </div>

            <div className="sm:col-span-2 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">Included Tutorials</h4>
                    <p className="text-sm text-gray-500">At least 2 tutorials required.</p>
                  </div>
                  <div className="rounded-full bg-pink-50 px-3 py-1 text-xs font-semibold text-pink-dark">
                    {form.course_ids.length} selected
                  </div>
                </div>

                <div className="space-y-2">
                  {selectedCourses.map((course, index) => (
                    <div
                      key={course.id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">{course.name}</div>
                        {course.short_description ? (
                          <p className="mt-1 line-clamp-2 text-xs text-gray-500">{course.short_description}</p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleMoveCourse(index, -1)}
                          disabled={index === 0}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-pink-dark disabled:opacity-30"
                          aria-label="Move up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveCourse(index, 1)}
                          disabled={index === selectedCourses.length - 1}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-pink-dark disabled:opacity-30"
                          aria-label="Move down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveCourse(course.id)}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
                          aria-label="Remove tutorial"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {selectedCourses.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-400">
                      No tutorials selected yet.
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <div className="mb-3">
                  <h4 className="font-semibold text-gray-900">Tutorial Picker</h4>
                  <p className="text-sm text-gray-500">Search the existing tutorial library and add them in order.</p>
                </div>

                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search tutorials"
                  className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                />

                <div className="max-h-[24rem] space-y-2 overflow-y-auto pr-1">
                  {coursesLoading ? (
                    <p className="text-sm text-gray-500">Loading tutorials…</p>
                  ) : availableCourses.length === 0 ? (
                    <p className="text-sm text-gray-400">No tutorials match this search.</p>
                  ) : (
                    availableCourses.map((course) => (
                      <div
                        key={course.id}
                        className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 px-3 py-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900">{course.name}</div>
                          {course.short_description ? (
                            <p className="mt-1 line-clamp-2 text-xs text-gray-500">{course.short_description}</p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddCourse(course.id)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#762a5c] bg-white px-3 py-1.5 text-xs font-semibold text-[#762a5c] transition-colors hover:bg-[#faf1f6]"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5">
            {error ? (
              <div ref={actionErrorRef} className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            {bundlesError ? (
              <div className="mb-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {bundlesError}
              </div>
            ) : null}

            {coursesError ? (
              <div className="mb-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Failed to load tutorials for the picker: {coursesError}
              </div>
            ) : null}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-pink-dark px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95 disabled:opacity-50"
              >
                {saving ? "Saving…" : `Save ${signatureRoutineLabel}`}
              </button>
              <button
                type="button"
                onClick={() => setEditingBundleId(null)}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-left text-sm">
            <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">{signatureRoutineLabel}</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Tutorials</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(bundles ?? []).map((bundle) => (
                <tr key={bundle.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{bundle.name}</div>
                    {bundle.short_description ? (
                      <div className="mt-1 max-w-[28rem] truncate text-xs text-gray-500">{bundle.short_description}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>${bundle.price}</div>
                    {bundle.discount_percentage != null ? (
                      <div className="mt-1 text-xs text-gray-500">{bundle.discount_percentage}% off</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{bundle.courses.length}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                        bundle.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {bundle.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingBundleId(bundle.id)}
                        className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-pink-dark"
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(bundle.id, bundle.name)}
                        disabled={deleting === bundle.id}
                        className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {(!bundles || bundles.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    No {signatureRoutinePluralLabelLower} yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatDiscountPercentage(value: number) {
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(2).replace(/\.?0+$/, "")}%`;
}
